import React from "react";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";

export default function EcoCreditsPage() {
  const user = useSelector((s) => s.auth.user) || {};
  const eco = user.ecoCredits || 0;
  const trees = Math.floor(eco / 50);
  const next = (trees + 1) * 50 - eco;

  const actions = [
    { title: "Rent a Book", img: "/images/rent book.svg", credits: 30 },
    { title: "Refer a Friend", img: "/images/refer friend.svg", credits: 50 },
    { title: "Leave a Review", img: "/images/leave review.svg", credits: 25 },
  ];

  const faqs = [
    {
      q: "What are EcoCredits?",
      a: "EcoCredits are a reward system that promotes eco-friendly actions like renting books, referring friends, and writing reviews. These actions earn you credits that can be redeemed later.",
    },
    {
      q: "Why should I care about EcoCredits?",
      a: "EcoCredits reward you with perks while contributing to the environment. Every 50 credits you earn means one tree saved!",
    },
    {
      q: "How can I earn EcoCredits?",
      a: (
        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
          <li>Renting a book (30 credits)</li>
          <li>Referring a friend (50 credits)</li>
          <li>Leaving a review (25 credits)</li>
          <li>Completing quizzes or surveys (coming soon)</li>
        </ul>
      ),
    },
    {
      q: "How can I redeem my EcoCredits?",
      a: "Once you collect enough credits, use the Redeem button to unlock discounts, free rentals, or sponsor a tree. Exciting perks are on the way!",
    },
  ];

  return (
    <motion.div
      className="max-w-6xl mx-auto p-6 sm:p-10 space-y-14"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Overview Section */}
      <section className="bg-white rounded-3xl p-8 shadow-xl grid grid-cols-1 md:grid-cols-3 gap-6 items-center text-center md:text-left">
        <div>
          <h2 className="text-xl font-bold text-gray-800">EcoCredits</h2>
          <p className="text-primary text-4xl font-extrabold mt-2">{eco}</p>
        </div>

        <div className="hidden md:block border-l h-20 border-gray-200 mx-auto" />

        <div>
          <h2 className="text-xl font-bold text-gray-800">Trees Saved</h2>
          <p className="text-primary text-4xl font-extrabold mt-2">{trees}</p>
          <p className="text-sm text-gray-500 italic mt-1">
            {next} more credits for next tree
          </p>
        </div>

        <div className="md:col-span-3 text-center">
          <button
            onClick={() => toast("Redeem feature coming soon!")}
            className="mt-6 bg-primary text-white px-6 py-3 rounded-full text-sm font-semibold shadow-lg hover:scale-105 transition"
          >
            Redeem Credits
          </button>
        </div>
      </section>

      {/* Earn More Credits */}
      <section>
        <h3 className="text-2xl font-semibold text-primary mb-6">Earn More Credits</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {actions.map((item) => (
            <div
              key={item.title}
              className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-lg transition"
            >
              <img src={item.img} alt={item.title} className="mx-auto h-24 mb-4" />
              <p className="text-sm font-medium text-gray-700">
                {item.title} : Earn {item.credits} Credits
              </p>

              {item.title === "Refer a Friend" && user.referralCode && (
                <div className="mt-4">
                  <p className="text-xs text-gray-600">Your Referral Code:</p>
                  <div className="mt-1 flex items-center justify-center gap-2">
                    <span className="text-sm font-semibold bg-gray-100 px-3 py-1 rounded-md border border-gray-300">
                      {user.referralCode}
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(user.referralCode);
                        toast.success("Referral code copied!");
                      }}
                      className="text-xs bg-primary text-white px-2 py-1 rounded hover:bg-primary-dark transition"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section>
        <h3 className="text-2xl font-semibold text-primary mb-6">FAQs – EcoCredits</h3>
        <div className="space-y-4">
          {faqs.map(({ q, a }) => (
            <details key={q} className="bg-white rounded-xl p-5 shadow-sm cursor-pointer">
              <summary className="text-sm font-semibold text-gray-800">{q}</summary>
              <div className="mt-2 text-sm text-gray-600">{a}</div>
            </details>
          ))}
        </div>
      </section>
    </motion.div>
  );
}
